<script setup lang="ts">
import { Icon } from '@iconify/vue'
import { ref, onMounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant'
import { PushCampaignAPI } from '../constants/push-campaign.api.uri.const'
import ConditionBuilder from '../components/ConditionBuilder.vue'

type AudienceClause = { key: string; op: string; value: unknown }
type AudienceNode =
  | { type: 'GROUP_AND' | 'GROUP_OR'; child: AudienceNode[] }
  | { type: 'CONDITION'; child: AudienceClause[] }

interface Props {
  campaignId: number | null
}

const props = defineProps<Props>()
const toast = useToast()

const linkTypeOptions = [
  { label: 'Liên kết tùy chỉnh', value: 'custom' },
  { label: 'Trang chủ', value: 'home' },
  { label: 'Tin tức', value: 'news' },
]

const hourOptions = Array.from({ length: 24 }, (_, i) => ({
  label: String(i).padStart(2, '0'),
  value: String(i).padStart(2, '0'),
}))
const minuteOptions = Array.from({ length: 60 }, (_, i) => ({
  label: String(i).padStart(2, '0'),
  value: String(i).padStart(2, '0'),
}))
const dayOptions = Array.from({ length: 31 }, (_, i) => ({
  label: String(i + 1).padStart(2, '0'),
  value: String(i + 1).padStart(2, '0'),
}))
const monthOptions = Array.from({ length: 12 }, (_, i) => ({
  label: String(i + 1).padStart(2, '0'),
  value: String(i + 1).padStart(2, '0'),
}))
const weekdayOptions = [
  { label: 'Thứ 2', value: '1' },
  { label: 'Thứ 3', value: '2' },
  { label: 'Thứ 4', value: '3' },
  { label: 'Thứ 5', value: '4' },
  { label: 'Thứ 6', value: '5' },
  { label: 'Thứ 7', value: '6' },
  { label: 'Chủ nhật', value: '7' },
]

const form = ref({
  campaign_type: 'notification_link',
  title: '',
  notification_title: '',
  notification_content: '',
  link_type: 'custom',
  link: '',
  target_audience: 'all',
  audience_conditions: null as AudienceNode | null,
  // Kế hoạch gửi
  schedule_type: 'now' as 'now' | 'recurring' | 'scheduled',
  // Lập lịch để gửi
  scheduled_date: '',
  scheduled_hour: '08',
  scheduled_minute: '00',
  // Gửi định kỳ
  recurring_type: 'daily' as 'daily' | 'weekly' | 'monthly' | 'yearly',
  recurring_hour: '08',
  recurring_minute: '00',
  recurring_weekday: '2',
  recurring_day: '01',
  recurring_month: '01',
})

onMounted(() => {
  if (props.campaignId) {
    // TODO: fetch detail from API when ready
    form.value = {
      campaign_type: 'notification_link',
      title: 'Tên chiến dịch',
      notification_title: 'Tiêu đề thông báo mẫu',
      notification_content: 'Nội dung thông báo mẫu cho chiến dịch đẩy',
      link_type: 'custom',
      link: 'https://example.com',
      target_audience: 'all',
      audience_conditions: null,
      schedule_type: 'now',
      scheduled_date: '2026-04-30',
      scheduled_hour: '01',
      scheduled_minute: '12',
      recurring_type: 'daily',
      recurring_hour: '08',
      recurring_minute: '00',
      recurring_weekday: '2',
      recurring_day: '01',
      recurring_month: '01',
    }
  }
})

const buildSchedulePayload = () => {
  const f = form.value

  if (f.schedule_type === 'now') {
    return {
      type: 'now',
      schedule: {
        hour: null,
        minute: null,
        weekday: null,
        date: null,
        month: null,
      },
    }
  }

  if (f.schedule_type === 'scheduled') {
    const [, m, d] = f.scheduled_date.split('-')
    return {
      type: 'future',
      schedule: {
        hour: Number(f.scheduled_hour),
        minute: Number(f.scheduled_minute),
        weekday: null,
        date: Number(d),
        month: Number(m),
      },
    }
  }

  // recurring
  const base = {
    hour: Number(f.recurring_hour),
    minute: Number(f.recurring_minute),
    weekday: null as number | null,
    date: null as number | null,
    month: null as number | null,
  }

  if (f.recurring_type === 'daily') {
    // chỉ cần hour + minute
  } else if (f.recurring_type === 'weekly') {
    base.weekday = Number(f.recurring_weekday)
  } else if (f.recurring_type === 'monthly') {
    base.date = Number(f.recurring_day)
  } else if (f.recurring_type === 'yearly') {
    base.date = Number(f.recurring_day)
    base.month = Number(f.recurring_month)
  }

  return {
    type: 'schedule',
    schedule: base,
  }
}

const handleSave = async () => {
  const schedulePayload = buildSchedulePayload()

  const payload = {
    campaign_type: form.value.campaign_type,
    title: form.value.title,
    notification_title: form.value.notification_title,
    notification_content: form.value.notification_content,
    link_type: form.value.link_type,
    link: form.value.link,
    target_audience: form.value.target_audience,
    audience_conditions: form.value.audience_conditions,
    ...schedulePayload,
  }

  console.log('[push-campaign] payload', payload)

  try {
    // TODO: replace with real API endpoint when BE is ready
    await APIClient.post(PushCampaignAPI.campaign.create, payload)
    toast.add({
      severity: 'success',
      summary: 'Thành công',
      detail: 'Lưu chiến dịch thành công',
      life: 3000,
    })
  } catch (error) {
    console.error('[push-campaign] save error', error)
    toast.add({
      severity: 'error',
      summary: 'Lỗi',
      detail: 'Lưu chiến dịch thất bại',
      life: 3000,
    })
  }
}
</script>

<template>
  <div class="flex gap-4 p-4">
    <!-- Left panel: campaign type -->
    <div class="shrink-0">
      <div class="campaign-card flex flex-col gap-4 p-4">
        <p class="font-bold text-[16px] text-black">
          Chọn loại chiến dịch <span class="text-[red]">*</span>
        </p>
        <label
          class="flex items-center gap-[10px] p-7 rounded-lg cursor-pointer border"
          :class="
            form.campaign_type === 'notification_link'
              ? 'border-[#0095ff] bg-[#e9f5ff]'
              : 'border-[#e2e2e2] bg-white'
          "
        >
          <input
            type="radio"
            name="campaign_type"
            value="notification_link"
            v-model="form.campaign_type"
            class="accent-[#0095ff] w-4 h-4"
          />
          <span class="font-bold text-[16px] text-black">Thông báo + liên kết</span>
        </label>
        <label
          class="flex items-center gap-[10px] p-7 rounded-lg cursor-pointer border"
          :class="
            form.campaign_type === 'notification_reward'
              ? 'border-[#0095ff] bg-[#e9f5ff]'
              : 'border-[#e2e2e2] bg-white'
          "
        >
          <input
            type="radio"
            name="campaign_type"
            value="notification_reward"
            v-model="form.campaign_type"
            class="accent-[#0095ff] w-4 h-4"
          />
          <span class="font-bold text-[16px] text-black">Thông báo + điểm thưởng</span>
        </label>
      </div>
    </div>

    <!-- Right panel: form sections -->
    <div class="flex-1 flex flex-col gap-4">
      <!-- Tiêu đề chiến dịch -->
      <div class="campaign-card flex flex-col gap-[10px] p-3">
        <p class="font-bold text-[16px] text-black">
          Tiêu đề chiến dịch <span class="text-[red]">*</span>
        </p>
        <InputText
          v-model="form.title"
          placeholder="Nhập tiêu đề chiến dịch"
          class="w-full campaign-input"
        />
      </div>

      <!-- Tiêu đề thông báo + Nội dung + Liên kết -->
      <div class="campaign-card flex flex-col gap-[10px] p-3">
        <p class="font-bold text-[16px] text-black">
          Tiêu đề thông báo <span class="text-[red]">*</span>
        </p>
        <InputText
          v-model="form.notification_title"
          placeholder="Nhập tiêu đề thông báo"
          class="w-full campaign-input"
        />

        <div class="h-[10px]" />

        <p class="font-bold text-[16px] text-black">
          Nội dung thông báo <span class="text-[red]">*</span>
        </p>
        <Textarea
          v-model="form.notification_content"
          placeholder="Nhập tiêu đề thông báo"
          rows="4"
          class="w-full campaign-input"
        />

        <div class="h-[10px]" />

        <template v-if="form.campaign_type === 'notification_link'">
          <p class="font-bold text-[16px] text-black">Liên kết <span class="text-[red]">*</span></p>
          <div class="flex gap-4 items-center">
            <Select
              v-model="form.link_type"
              :options="linkTypeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Chọn loại liên kết"
              class="w-[179px] shrink-0 campaign-input"
            />
            <InputText
              v-model="form.link"
              placeholder="Nhập liên kết tùy chỉnh"
              class="flex-1 campaign-input"
            />
          </div>
        </template>
      </div>

      <!-- Đối tượng áp dụng -->
      <div class="campaign-card flex flex-col gap-[10px] p-3">
        <p class="font-bold text-[16px] text-black">
          Đối tượng áp dụng <span class="text-[red]">*</span>
        </p>
        <ConditionBuilder v-model="form.audience_conditions" />
      </div>

      <!-- Kế hoạch gửi -->
      <div class="campaign-card flex flex-col gap-[10px] p-3">
        <p class="font-bold text-[16px] text-black">
          Kế hoạch gửi <span class="text-[red]">*</span>
        </p>

        <div class="h-[10px]" />

        <!-- Hình thức gửi -->
        <div class="flex items-center gap-4">
          <span class="font-bold text-[12px] text-black shrink-0">Hình thức gửi</span>
          <label class="flex items-center gap-[7px] cursor-pointer">
            <input
              type="radio"
              v-model="form.schedule_type"
              value="now"
              class="accent-[#0095ff] w-[14px] h-[14px]"
            />
            <span class="text-[12px] text-black">Gửi ngay lập tức</span>
          </label>
          <label class="flex items-center gap-[7px] cursor-pointer">
            <input
              type="radio"
              v-model="form.schedule_type"
              value="recurring"
              class="accent-[#0095ff] w-[14px] h-[14px]"
            />
            <span class="text-[12px] text-black">Gửi định kỳ</span>
          </label>
          <label class="flex items-center gap-[7px] cursor-pointer">
            <input
              type="radio"
              v-model="form.schedule_type"
              value="scheduled"
              class="accent-[#0095ff] w-[14px] h-[14px]"
            />
            <span class="text-[12px] text-black">Lập lịch để gửi</span>
          </label>
        </div>

        <div class="h-[10px]" />

        <!-- Trạng thái: Lập lịch để gửi -->
        <div
          v-if="form.schedule_type === 'scheduled'"
          class="bg-[#fafafa] flex items-center gap-5 p-4"
        >
          <span class="font-bold text-[12px] text-black shrink-0"
            >Chọn ngày gửi trong tương lai</span
          >
          <div class="bg-white flex items-center gap-[10px] px-4 py-2 rounded">
            <input
              type="date"
              v-model="form.scheduled_date"
              class="text-[13px] text-black border-none outline-none bg-transparent cursor-pointer"
            />
          </div>
          <span class="text-[12px] text-black">vào lúc</span>
          <div class="flex items-center gap-[7px]">
            <Select
              v-model="form.scheduled_hour"
              :options="hourOptions"
              optionLabel="label"
              optionValue="value"
              class="schedule-select"
            />
            <span class="text-[16px] text-black">:</span>
            <Select
              v-model="form.scheduled_minute"
              :options="minuteOptions"
              optionLabel="label"
              optionValue="value"
              class="schedule-select"
            />
          </div>
        </div>

        <!-- Trạng thái: Gửi định kỳ -->
        <div
          v-if="form.schedule_type === 'recurring'"
          class="bg-[#fafafa] flex items-start gap-5 p-4"
        >
          <span class="font-bold text-[12px] text-black shrink-0 mt-[10px]"
            >Cài đặt gửi định kỳ</span
          >
          <div class="flex flex-col gap-[22px]">
            <!-- Hàng ngày -->
            <div class="flex items-center gap-4">
              <label class="flex items-center gap-[7px] cursor-pointer shrink-0">
                <input
                  type="radio"
                  v-model="form.recurring_type"
                  value="daily"
                  class="accent-[#0095ff] w-[14px] h-[14px]"
                />
                <span class="text-[12px] text-black">Gửi hàng ngày vào lúc</span>
              </label>
              <div class="flex items-center gap-[7px]">
                <Select
                  v-model="form.recurring_hour"
                  :options="hourOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
                <span class="text-[16px] text-black">:</span>
                <Select
                  v-model="form.recurring_minute"
                  :options="minuteOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
              </div>
            </div>

            <!-- Hàng tuần -->
            <div class="flex items-center gap-4">
              <label class="flex items-center gap-[7px] cursor-pointer shrink-0">
                <input
                  type="radio"
                  v-model="form.recurring_type"
                  value="weekly"
                  class="accent-[#0095ff] w-[14px] h-[14px]"
                />
                <span class="text-[12px] text-black">Gửi hàng tuần mỗi ngày</span>
              </label>
              <Select
                v-model="form.recurring_weekday"
                :options="weekdayOptions"
                optionLabel="label"
                optionValue="value"
                class="schedule-select"
              />
              <span class="text-[12px] text-black">vào lúc</span>
              <div class="flex items-center gap-[7px]">
                <Select
                  v-model="form.recurring_hour"
                  :options="hourOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
                <span class="text-[16px] text-black">:</span>
                <Select
                  v-model="form.recurring_minute"
                  :options="minuteOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
              </div>
            </div>

            <!-- Hàng tháng -->
            <div class="flex items-center gap-4">
              <label class="flex items-center gap-[7px] cursor-pointer shrink-0">
                <input
                  type="radio"
                  v-model="form.recurring_type"
                  value="monthly"
                  class="accent-[#0095ff] w-[14px] h-[14px]"
                />
                <span class="text-[12px] text-black">Gửi hàng tháng vào ngày</span>
              </label>
              <Select
                v-model="form.recurring_day"
                :options="dayOptions"
                optionLabel="label"
                optionValue="value"
                class="schedule-select"
              />
              <span class="text-[12px] text-black">vào lúc</span>
              <div class="flex items-center gap-[7px]">
                <Select
                  v-model="form.recurring_hour"
                  :options="hourOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
                <span class="text-[16px] text-black">:</span>
                <Select
                  v-model="form.recurring_minute"
                  :options="minuteOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
              </div>
            </div>

            <!-- Hàng năm -->
            <div class="flex items-center gap-4">
              <label class="flex items-center gap-[7px] cursor-pointer shrink-0">
                <input
                  type="radio"
                  v-model="form.recurring_type"
                  value="yearly"
                  class="accent-[#0095ff] w-[14px] h-[14px]"
                />
                <span class="text-[12px] text-black">Gửi hàng năm vào ngày</span>
              </label>
              <div class="flex items-center gap-[7px]">
                <Select
                  v-model="form.recurring_day"
                  :options="dayOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
                <span class="text-[16px] text-black">/</span>
                <Select
                  v-model="form.recurring_month"
                  :options="monthOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
              </div>
              <span class="text-[12px] text-black">vào lúc</span>
              <div class="flex items-center gap-[7px]">
                <Select
                  v-model="form.recurring_hour"
                  :options="hourOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
                <span class="text-[16px] text-black">:</span>
                <Select
                  v-model="form.recurring_minute"
                  :options="minuteOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="schedule-select"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Save button -->
      <div class="campaign-card flex items-center justify-end p-4">
        <button
          class="flex items-center gap-[10px] bg-[#008724] hover:bg-[#006e1d] text-white font-bold text-[10px] px-4 py-[7px] rounded-lg cursor-pointer"
          @click="handleSave"
        >
          <Icon icon="mingcute:save-fill" height="24px" />
          Lưu chiến dịch
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.campaign-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.14);
  overflow: clip;
}

:deep(.campaign-input.p-inputtext),
:deep(.campaign-input .p-inputtext),
:deep(.campaign-input.p-textarea),
:deep(.campaign-input .p-select-label) {
  background: #f7f7f7;
  border: none;
  font-size: 16px;
  padding: 16px;
  color: #333;
}

:deep(.campaign-input.p-textarea) {
  min-height: 129px;
}

:deep(.schedule-select.p-select) {
  background: white;
  border: none;
  border-radius: 4px;
  min-width: unset;
  height: 40px;
}

:deep(.schedule-select .p-select-label) {
  background: white;
  border: none;
  font-size: 13px;
  padding: 0 16px;
  line-height: 40px;
}

:deep(.schedule-select .p-select-dropdown) {
  width: auto;
  padding-right: 16px;
}

:deep(.campaign-input.p-select),
:deep(.campaign-input.p-select .p-select-label) {
  background: #f7f7f7;
  border: none;
  font-size: 16px;
}

:deep(.campaign-input.p-inputtext::placeholder),
:deep(.campaign-input .p-inputtext::placeholder),
:deep(.campaign-input.p-textarea::placeholder),
:deep(.campaign-input .p-select-label.p-placeholder) {
  color: #777;
  font-size: 16px;
}
</style>
